import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

// Convert a Base64-URL-encoded VAPID public key to the Uint8Array required by the browser.
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export function usePushSupport() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function usePushNotifications() {
  const supported = usePushSupport();
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );

  const refresh = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    } catch (_) {
      setSubscribed(false);
    }
  }, [supported]);

  useEffect(() => { refresh(); }, [refresh]);

  const enable = useCallback(async () => {
    if (!supported) {
      toast.error("Push isn't supported on this browser");
      return false;
    }
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Notification permission denied");
        return false;
      }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-public-key");
      if (!data.public_key) {
        toast.error("Push not configured on the server");
        return false;
      }
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.public_key),
        });
      }
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys || {},
      });
      setSubscribed(true);
      toast.success("Mission alerts on. We'll ping you Monday morning.");
      return true;
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Could not enable push");
      return false;
    } finally {
      setBusy(false);
    }
  }, [supported]);

  const disable = useCallback(async () => {
    if (!supported) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const json = sub.toJSON();
        try {
          await api.post("/push/unsubscribe", {
            endpoint: json.endpoint,
            keys: json.keys || {},
          });
        } catch (_) {}
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Mission alerts off");
    } finally {
      setBusy(false);
    }
  }, [supported]);

  return { supported, subscribed, busy, permission, enable, disable, refresh };
}
